import { useMemo } from "react";
import { useDebouncedValue } from "@hooks/useDebouncedValue";

export interface ClientPaginatedListFilters {
  page: number;
  pageSize: number;
  search?: string;
}

export interface ClientPaginatedListResult<T = any> {
  items: T[];
  totalItems: number;
  totalPages: number;
}

export interface ClientPaginatedListOptions<T = any> {
  searchPredicate?: (item: T, search: string) => boolean;
  searchDebounceMs?: number;
}

/**
 * Paginates (and optionally filters) an already-fetched array on the client.
 * Use this when the source endpoint has no server-side pagination contract
 * (no pageIndex/pageSize support, no output/metadata response shape) —
 * for that case use `usePaginatedList` instead.
 */
export const useClientPaginatedList = <T = any>(
  data: T[] | undefined | null,
  filters: ClientPaginatedListFilters,
  options: ClientPaginatedListOptions<T> = {},
): ClientPaginatedListResult<T> => {
  const debouncedSearch = useDebouncedValue(
    filters.search,
    options.searchDebounceMs ?? 400,
  );

  const filtered = useMemo(() => {
    const list = data || [];
    if (!debouncedSearch || !options.searchPredicate) return list;
    return list.filter((item) =>
      options.searchPredicate!(item, debouncedSearch),
    );
  }, [data, debouncedSearch, options.searchPredicate]);

  const totalItems = filtered.length;
  const totalPages = Math.max(Math.ceil(totalItems / filters.pageSize), 1);

  const items = useMemo(() => {
    const start = Math.max(filters.page - 1, 0) * filters.pageSize;
    return filtered.slice(start, start + filters.pageSize);
  }, [filtered, filters.page, filters.pageSize]);

  return { items, totalItems, totalPages };
};
